import React, { useEffect, useState, useCallback } from "react";
import { Skeleton } from "antd";
import { useParams } from "react-router-dom";
import { IAuditLogResponse, IAuditLogResult } from "../../interfaces/audit-log";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { AuditLogServices } from "../../services/audit-log";
import { ISortInfo } from "../../interfaces/common";
import { formatDateDDMMYYYY, formatDateDDMMYYYY_HHMMSS, formatNumberFixed } from "../../utils/helper";
import BackButton from "../../components/back-button";
import { AuditItem } from "../../components/audit-trail/audit-trail";
import InfiniteAuditTrail from "../../components/audit-trail/infinite-audit-trail";

const fieldLabels: Record<string, string> = {
  supplier_id: "Supplier Id",
  supplier_code: "Supplier Code",
  supplier_name: "Supplier Name",
  booking_ref: "Booking Ref",
  currency: "Currency",
  amount: "Amount",
  due_date: "Due Date",
  is_active: "Status", // old data
  status: "Status",
  generated_by: "Generated By",
  stage_type: "Stage Type",
  stage_order: "Stage Order",
  gbp_cost: "GBP Cost",
  type: "Workflow",
};

const EspAudiTrailPage: React.FC = () => {
  const [logs, setLogs] = useState<IAuditLogResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [sortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });

  const { id } = useParams<{ id: string }>();

  const fetchData = async () => {
    if (!id) {
      setLogs([]);
      setInitialLoading(false);
      return;
    }
    if (!hasMore && pagination.current !== 1) return;

    try {
      if (pagination.current === 1) setInitialLoading(true);
      else setLoading(true);

      const params = {
        tableName: "expected_supplier_payments",
        recordId: id,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };

      const response: IAuditLogResponse = await AuditLogServices.getAuditLogs({ params });
      const newLogs = response.data.results || [];

      setLogs(prev => (pagination.current === 1 ? newLogs : [...prev, ...newLogs]));
      setPagination(prev => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
      setHasMore(newLogs.length === pagination.pageSize);
    } catch (error) {
      console.error("Fetch audit logs error:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    fetchData();
   // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [id, pagination?.current, pagination?.pageSize, sortInfo, hasMore]);

  const handleLoadMore = () => {
    setPagination(prev => ({ ...prev, current: prev.current + 1 }));
  };

  const formatValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === "") return "blank";
    switch (field) {
      case "is_active":
        return value ? "Active" : "Inactive";
      case "amount":
        return formatNumberFixed(value) ?? "blank";
      case "due_date":
        return formatDateDDMMYYYY(value) ?? "blank";
      default:
        if (typeof value === "boolean") return value ? "Yes" : "No";
        return String(value);
    }
  };

  const auditData: AuditItem[] = logs.map(log => ({
    id: log.id,
    action: log.action,
    changes: log.changes.map(c => ({
      field: fieldLabels[c.field] || c.field,
      from: formatValue(c.field, c.oldValue),
      to: formatValue(c.field, c.newValue),
    })),
    updatedAt: formatDateDDMMYYYY_HHMMSS(log.updatedAt)!,
    user: log.updatedByUser || "System",
  }));

  return (
    <div className="audit-trail-page">
      <BackButton label="Expected Supplier Payment" />
      <h2 className="title-page" style={{ marginBottom: 16 }}>Audit Trail</h2>
      <div style={{ borderBottom: "1px solid #e8e8e8", marginBottom: 30 }}></div>

      {initialLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <InfiniteAuditTrail
          items={auditData}
          loading={loading}
          hasMore={hasMore}
          initialLoading={initialLoading}
          onLoadMore={handleLoadMore}
        />
      )}
    </div>
  );
};

export default EspAudiTrailPage;
